// Server keys (sk_live_...): secret, org-owned keys for server-to-server
// calls to the agent runs API, limited to the assistants they list.
// Managed by org admins via /api/server-keys. Never embed one in a browser.
import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert, Chip, Tooltip, FormControl, InputLabel,
  Select, MenuItem, OutlinedInput, Checkbox, ListItemText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { API_BASE_URL } from '../../../config/api';
import useIsMobile from '../useIsMobile';
import './AssistantSettingsTheme.css';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const errorDetail = async (response, fallback) => {
  const body = await response.json().catch(() => ({}));
  if (typeof body.detail === 'string') return body.detail;
  if (Array.isArray(body.detail)) return body.detail.map((d) => d.msg).join('; ');
  return fallback;
};

const ServerKeyManagement = () => {
  const isMobile = useIsMobile();
  const [keys, setKeys] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create / edit dialog. editingKey null = creating.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [name, setName] = useState('');
  const [agentIds, setAgentIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createdKey, setCreatedKey] = useState(null); // { name, server_key }

  const agentName = (id) => agents.find((a) => String(a.id) === String(id))?.name || 'Deleted assistant';

  const fetchKeys = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/server-keys`, { headers: authHeaders() });
      if (!res.ok) throw new Error(await errorDetail(res, 'Failed to load server keys'));
      setKeys(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKeys();
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/agents`, { headers: authHeaders() });
        if (res.ok) setAgents((await res.json()).assistants || []);
      } catch (e) { /* the agent picker just stays empty */ }
    })();
  }, []);

  const openCreate = () => {
    setEditingKey(null);
    setName('');
    setAgentIds([]);
    setDialogOpen(true);
  };

  const openEdit = (key) => {
    setEditingKey(key);
    setName(key.name);
    setAgentIds(key.allowed_agent_ids.map(String));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const body = JSON.stringify({ name: name.trim(), agent_ids: agentIds });
      const res = editingKey
        ? await fetch(`${API_BASE_URL}/api/server-keys/${editingKey.id}`, { method: 'PATCH', headers: authHeaders(), body })
        : await fetch(`${API_BASE_URL}/api/server-keys`, { method: 'POST', headers: authHeaders(), body });
      if (!res.ok) throw new Error(await errorDetail(res, 'Failed to save server key'));
      const data = await res.json();
      if (!editingKey) setCreatedKey({ name: data.name, server_key: data.server_key });
      setDialogOpen(false);
      fetchKeys();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (key) => {
    if (!window.confirm(`Revoke "${key.name}"? Any server using it will stop working immediately.`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/server-keys/${key.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error(await errorDetail(res, 'Failed to revoke key'));
      fetchKeys();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Box className="assistantSettingsRoot">
      <Box className="assistantSettingsHeaderRow">
        <Typography variant="h6" className="assistantSettingsTitle">Server Keys</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} className="assistantPrimaryButton">
          Create Server Key
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Secret keys for your own servers to call the agent runs API. Each key only works for the assistants you choose. Never put one in a website or app.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {createdKey && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setCreatedKey(null)}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Server key created: {createdKey.name}</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>Copy it now. You will not be able to see it again.</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
            <TextField size="small" fullWidth value={createdKey.server_key} InputProps={{ readOnly: true }} className="assistantMonoInput" />
            <Button variant="outlined" className="assistantOutlineButton" startIcon={<ContentCopyIcon />}
              onClick={() => navigator.clipboard.writeText(createdKey.server_key)}>Copy</Button>
          </Box>
        </Alert>
      )}

      <TableContainer component={Paper} elevation={0} className="assistantTablePanel assistantTableScroll">
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              {!isMobile && <TableCell>Prefix</TableCell>}
              <TableCell>Assistants</TableCell>
              {!isMobile && <TableCell>Last used</TableCell>}
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 4 : 6} align="center">No server keys yet.</TableCell>
              </TableRow>
            ) : keys.map((key) => (
              <TableRow key={key.id}>
                <TableCell>{key.name}</TableCell>
                {!isMobile && (
                  <TableCell><Chip label={`${key.prefix}...`} size="small" variant="outlined" className="assistantMonoChip" /></TableCell>
                )}
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {key.allowed_agent_ids.map((id) => <Chip key={id} label={agentName(id)} size="small" />)}
                  </Box>
                </TableCell>
                {!isMobile && (
                  <TableCell>{key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}</TableCell>
                )}
                <TableCell>{key.is_active ? 'Active' : 'Revoked'}</TableCell>
                <TableCell align="right">
                  {key.is_active && (
                    <>
                      <Tooltip title="Edit name and assistants">
                        <IconButton size="small" onClick={() => openEdit(key)}><EditIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Revoke key">
                        <IconButton size="small" className="assistantIconButtonDanger" onClick={() => handleRevoke(key)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullScreen={isMobile} fullWidth
        PaperProps={{ className: 'assistantDialogPaper' }}>
        <DialogTitle className="assistantDialogTitle">{editingKey ? 'Edit Server Key' : 'Create Server Key'}</DialogTitle>
        <DialogContent>
          <TextField sx={{ mt: 1 }} autoFocus margin="dense" label="Key name (e.g., YouthReach WordPress)" fullWidth
            value={name} onChange={(e) => setName(e.target.value)} />
          <FormControl fullWidth margin="dense">
            <InputLabel id="server-key-agents">Assistants this key can run</InputLabel>
            <Select labelId="server-key-agents" multiple value={agentIds}
              onChange={(e) => setAgentIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              input={<OutlinedInput label="Assistants this key can run" />}
              renderValue={(selected) => selected.map(agentName).join(', ')}>
              {agents.map((a) => (
                <MenuItem key={a.id} value={String(a.id)}>
                  <Checkbox checked={agentIds.includes(String(a.id))} />
                  <ListItemText primary={a.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className="assistantDialogActions">
          <Button onClick={() => setDialogOpen(false)} variant="outlined" className="assistantOutlineButton">Cancel</Button>
          <Button onClick={handleSave} variant="contained" className="assistantPrimaryButton"
            disabled={saving || !name.trim() || agentIds.length === 0}>
            {saving ? 'Saving...' : editingKey ? 'Save' : 'Create Key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServerKeyManagement;
